/**
 * ApiAccessManagement — Administrer API-tilganger for /api/v1
 *
 * Kun synlig for systemadmin. Lar admin opprette, oppdatere, slette
 * api_credentials, regenerere nøkler, og se webhook-registreringer.
 *
 * Inkluderer også nedlasting av WordPress-plugin og lenke til Swagger UI.
 */

import { useState, useEffect } from 'react';
import {
  Key, Plus, Trash2, RotateCcw, Download, ExternalLink, Eye, EyeOff,
  Shield, Building2, User, Globe, AlertTriangle, CheckCircle2, Webhook,
  Copy, Loader2, BookOpen
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface Credential {
  id: string;
  api_username: string;
  is_systemadmin: boolean;
  member_id: string | null;
  study_association_id: string | null;
  organization_name: string | null;
  study_association_name: string | null;
  status: string;
  rate_limit_per_hour: number;
  allowed_ips: string[] | null;
  last_used_at: string | null;
  created_at: string;
  active_webhook_count: number;
  metadata: { notes?: string } | null;
}

interface Webhook {
  id: string;
  target_url: string;
  events: string[];
  status: string;
  created_at: string;
  last_triggered_at: string | null;
  last_response_status: number | null;
  failure_count: number;
}

interface Member {
  id: string;
  organization_name: string;
  study_association_id: string;
}

interface StudyAssociation {
  id: string;
  name: string;
}

const SCOPE_LABEL: Record<string, { label: string; icon: typeof Shield; color: string }> = {
  systemadmin: { label: 'Systemadmin (full tilgang)', icon: Shield, color: 'text-red-700 bg-red-50 border-red-200' },
  forbund:     { label: 'Studieforbund',                icon: Building2, color: 'text-kf-blue-700 bg-kf-blue-50 border-kf-blue-200' },
  member:      { label: 'Medlemsorganisasjon',          icon: User, color: 'text-green-700 bg-green-50 border-green-200' }
};

function getScope(c: Credential): 'systemadmin' | 'forbund' | 'member' | 'unknown' {
  if (c.is_systemadmin) return 'systemadmin';
  if (c.member_id) return 'member';
  if (c.study_association_id) return 'forbund';
  return 'unknown';
}

export default function ApiAccessManagement() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [associations, setAssociations] = useState<StudyAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyDialog, setNewKeyDialog] = useState<{ username: string; key: string } | null>(null);
  const [expandedCredId, setExpandedCredId] = useState<string | null>(null);
  const [credWebhooks, setCredWebhooks] = useState<Record<string, Webhook[]>>({});

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    setError(null);
    try {
      const [credsRes, membersRes, assocRes] = await Promise.all([
        authenticatedFetch('/api/admin/api-credentials'),
        authenticatedFetch('/api/admin/members'),
        authenticatedFetch('/api/study-associations')
      ]);
      const credsData = await credsRes.json();
      if (credsData.success) setCredentials(credsData.credentials);
      const membersData = await membersRes.json();
      if (membersData.success || Array.isArray(membersData)) {
        setMembers(membersData.members || membersData || []);
      }
      const assocData = await assocRes.json();
      if (Array.isArray(assocData)) setAssociations(assocData);
      else if (assocData.study_associations) setAssociations(assocData.study_associations);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke laste data');
    } finally {
      setLoading(false);
    }
  }

  async function loadWebhooksFor(credId: string) {
    if (credWebhooks[credId]) return;
    try {
      const res = await authenticatedFetch(`/api/admin/api-credentials/${credId}/webhooks`);
      const data = await res.json();
      if (data.success) {
        setCredWebhooks(prev => ({ ...prev, [credId]: data.webhooks }));
      }
    } catch (e) { /* svelg */ }
  }

  async function handleCreate(form: any) {
    try {
      const res = await authenticatedFetch('/api/admin/api-credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setNewKeyDialog({ username: data.credential.api_username, key: data.api_key });
      setShowCreate(false);
      fetchAll();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Opprettelse feilet');
    }
  }

  async function handleStatusChange(id: string, status: string) {
    if (!confirm(`Endre status til "${status}"?`)) return;
    try {
      const res = await authenticatedFetch(`/api/admin/api-credentials/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      fetchAll();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Oppdatering feilet');
    }
  }

  async function handleRegenerate(id: string, username: string) {
    if (!confirm(`Generere ny nøkkel for "${username}"? Den gamle nøkkelen blir ugyldig umiddelbart.`)) return;
    try {
      const res = await authenticatedFetch(`/api/admin/api-credentials/${id}/regenerate-key`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setNewKeyDialog({ username, key: data.api_key });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Regenerering feilet');
    }
  }

  async function handleDelete(id: string, username: string) {
    if (!confirm(`Slette credential "${username}"? Dette kan ikke angres.`)) return;
    try {
      const res = await authenticatedFetch(`/api/admin/api-credentials/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      fetchAll();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Sletting feilet');
    }
  }

  async function handleDeleteWebhook(credId: string, webhookId: string) {
    if (!confirm('Slette webhook?')) return;
    try {
      await authenticatedFetch(`/api/admin/api-credentials/${credId}/webhooks/${webhookId}`, {
        method: 'DELETE'
      });
      setCredWebhooks(prev => {
        const next = { ...prev };
        next[credId] = (next[credId] || []).filter(w => w.id !== webhookId);
        return next;
      });
      fetchAll();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Sletting feilet');
    }
  }

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-kf-blue-600" /></div>;
  if (error) return <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">{error}</div>;

  return (
    <div className="space-y-6">
      {/* Header med dokumentasjons- og nedlastingslenker */}
      <div className="bg-gradient-to-br from-kf-blue-50 to-white border border-kf-blue-200 rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-kf-blue-900 flex items-center gap-2">
              <Key className="w-5 h-5" /> API-tilganger og dokumentasjon
            </h2>
            <p className="text-sm text-kf-gray-700 mt-1">
              Administrer API-credentials for eksterne LMS, kursleverandører og partnere.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href="/api/v1/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-kf-blue-300 hover:bg-kf-blue-50 rounded-md text-sm font-medium text-kf-blue-700"
            >
              <BookOpen className="w-4 h-4" /> Swagger UI <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href="/api/v1/openapi.json"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-kf-blue-300 hover:bg-kf-blue-50 rounded-md text-sm font-medium text-kf-blue-700"
            >
              <Globe className="w-4 h-4" /> OpenAPI 3.1 <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href="/api/v1/wordpress-plugin/download"
              className="inline-flex items-center gap-2 px-4 py-2 bg-kf-orange-600 hover:bg-kf-orange-700 text-white rounded-md text-sm font-medium"
            >
              <Download className="w-4 h-4" /> Last ned WordPress-plugin
            </a>
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white border border-kf-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-kf-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-kf-gray-900">API-credentials ({credentials.length})</h3>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded-md text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Opprett ny credential
          </button>
        </div>

        {credentials.length === 0 ? (
          <div className="p-8 text-center text-kf-gray-500">
            Ingen credentials opprettet enda. Klikk "Opprett ny credential" for å gi en partner tilgang.
          </div>
        ) : (
          <div className="divide-y divide-kf-gray-200">
            {credentials.map(c => {
              const scope = getScope(c);
              const meta = SCOPE_LABEL[scope] || SCOPE_LABEL.member;
              const Icon = meta.icon;
              const expanded = expandedCredId === c.id;
              return (
                <div key={c.id} className="p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="font-mono text-sm font-bold text-kf-blue-900">{c.api_username}</code>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
                          <Icon className="w-3 h-3" />
                          {meta.label}
                        </span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === 'active' ? 'bg-green-100 text-green-800' :
                          c.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>{c.status}</span>
                      </div>
                      <div className="text-xs text-kf-gray-600 space-x-3">
                        {c.organization_name && <span>📦 {c.organization_name}</span>}
                        {c.study_association_name && !c.organization_name && <span>🏛 {c.study_association_name}</span>}
                        <span>⚡ {c.rate_limit_per_hour}/time</span>
                        <span>🔗 {c.active_webhook_count} aktive webhooks</span>
                        {c.last_used_at ? (
                          <span>🕒 Sist brukt: {new Date(c.last_used_at).toLocaleString('nb-NO')}</span>
                        ) : (
                          <span className="text-kf-gray-400">Aldri brukt</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedCredId(expanded ? null : c.id);
                          if (!expanded) loadWebhooksFor(c.id);
                        }}
                        className="text-xs text-kf-gray-600 hover:text-kf-blue-700 px-2 py-1"
                      >
                        {expanded ? 'Skjul detaljer' : 'Vis detaljer'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRegenerate(c.id, c.api_username)}
                        className="text-xs text-kf-blue-600 hover:text-kf-blue-800 px-2 py-1 inline-flex items-center gap-1"
                        title="Generer ny API-nøkkel"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Ny nøkkel
                      </button>
                      <select
                        value={c.status}
                        onChange={(e) => handleStatusChange(c.id, e.target.value)}
                        className="text-xs border border-kf-gray-300 rounded px-2 py-1"
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="revoked">Revoked</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id, c.api_username)}
                        className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded"
                        title="Slett credential"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {expanded && (
                    <div className="mt-3 pt-3 border-t border-kf-gray-100 space-y-3 text-sm">
                      {c.allowed_ips && c.allowed_ips.length > 0 && (
                        <div>
                          <strong className="text-xs uppercase text-kf-gray-500">IP-whitelist:</strong>
                          <div className="font-mono text-xs text-kf-gray-700 mt-1">{c.allowed_ips.join(', ')}</div>
                        </div>
                      )}
                      {c.metadata?.notes && (
                        <div>
                          <strong className="text-xs uppercase text-kf-gray-500">Notater:</strong>
                          <div className="text-xs text-kf-gray-700 mt-1">{c.metadata.notes}</div>
                        </div>
                      )}
                      <div>
                        <strong className="text-xs uppercase text-kf-gray-500 flex items-center gap-1">
                          <Webhook className="w-3 h-3" /> Webhooks ({(credWebhooks[c.id] || []).length})
                        </strong>
                        {!credWebhooks[c.id] ? (
                          <p className="text-xs text-kf-gray-400 mt-1">Laster…</p>
                        ) : credWebhooks[c.id].length === 0 ? (
                          <p className="text-xs text-kf-gray-500 mt-1">Ingen registrerte webhooks.</p>
                        ) : (
                          <div className="space-y-1 mt-1">
                            {credWebhooks[c.id].map(w => (
                              <div key={w.id} className="flex items-center justify-between gap-2 p-2 bg-kf-gray-50 rounded text-xs">
                                <div className="flex-1 min-w-0">
                                  <code className="block truncate text-kf-gray-700">{w.target_url}</code>
                                  <div className="flex gap-2 mt-0.5 text-kf-gray-500">
                                    <span>Events: {w.events.join(', ')}</span>
                                    <span>•</span>
                                    <span className={w.status === 'active' ? 'text-green-700' : 'text-red-700'}>{w.status}</span>
                                    {w.failure_count > 0 && <span className="text-kf-orange-700">⚠ {w.failure_count} feil</span>}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteWebhook(c.id, w.id)}
                                  className="text-red-600 hover:bg-red-50 p-1 rounded"
                                  title="Slett webhook"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Opprettelses-modal */}
      {showCreate && (
        <CreateCredentialModal
          members={members}
          associations={associations}
          onCancel={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}

      {/* Vis-nøkkel-modal */}
      {newKeyDialog && (
        <NewKeyModal
          username={newKeyDialog.username}
          apiKey={newKeyDialog.key}
          onClose={() => setNewKeyDialog(null)}
        />
      )}
    </div>
  );
}

// ----- Modaler -----

function CreateCredentialModal({ members, associations, onCancel, onCreate }: {
  members: Member[];
  associations: StudyAssociation[];
  onCancel: () => void;
  onCreate: (form: any) => void;
}) {
  const [username, setUsername] = useState('');
  const [scope, setScope] = useState<'systemadmin' | 'forbund' | 'member'>('member');
  const [memberId, setMemberId] = useState('');
  const [associationId, setAssociationId] = useState('');
  const [rateLimit, setRateLimit] = useState(1000);
  const [allowedIps, setAllowedIps] = useState('');
  const [notes, setNotes] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const form: any = {
      api_username: username.trim(),
      scope,
      rate_limit_per_hour: rateLimit,
      notes: notes.trim() || null
    };
    if (scope === 'member') form.member_id = memberId;
    if (scope === 'forbund') form.study_association_id = associationId;
    if (allowedIps.trim()) form.allowed_ips = allowedIps.split(',').map(s => s.trim()).filter(Boolean);
    onCreate(form);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto">
        <h3 className="text-lg font-bold text-kf-blue-900 mb-4">Opprett ny API-credential</h3>

        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-kf-gray-700">API-brukernavn</span>
            <input
              type="text"
              required
              pattern="[a-zA-Z0-9_-]{3,80}"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 w-full border border-kf-gray-300 rounded-md px-3 py-2"
              placeholder="f.eks. aof_oslo_prod"
            />
            <span className="text-xs text-kf-gray-500">3-80 tegn, a-z, A-Z, 0-9, _, -</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-kf-gray-700">Tilgangsnivå</span>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as any)}
              className="mt-1 w-full border border-kf-gray-300 rounded-md px-3 py-2"
            >
              <option value="member">Medlemsorganisasjon — kun egne studieplaner</option>
              <option value="forbund">Studieforbund — alle planer i forbundet</option>
              <option value="systemadmin">Systemadmin — full tilgang (kun for formelle partnere)</option>
            </select>
          </label>

          {scope === 'member' && (
            <label className="block">
              <span className="text-sm font-medium text-kf-gray-700">Medlemsorganisasjon</span>
              <select required value={memberId} onChange={(e) => setMemberId(e.target.value)} className="mt-1 w-full border border-kf-gray-300 rounded-md px-3 py-2">
                <option value="">Velg organisasjon…</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.organization_name}</option>)}
              </select>
            </label>
          )}

          {scope === 'forbund' && (
            <label className="block">
              <span className="text-sm font-medium text-kf-gray-700">Studieforbund</span>
              <select required value={associationId} onChange={(e) => setAssociationId(e.target.value)} className="mt-1 w-full border border-kf-gray-300 rounded-md px-3 py-2">
                <option value="">Velg studieforbund…</option>
                {associations.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-sm font-medium text-kf-gray-700">Rate-limit (forespørsler/time)</span>
            <input
              type="number"
              min={10}
              max={100000}
              value={rateLimit}
              onChange={(e) => setRateLimit(parseInt(e.target.value) || 1000)}
              className="mt-1 w-full border border-kf-gray-300 rounded-md px-3 py-2"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-kf-gray-700">Tillatte IP-adresser (valgfritt)</span>
            <input
              type="text"
              value={allowedIps}
              onChange={(e) => setAllowedIps(e.target.value)}
              className="mt-1 w-full border border-kf-gray-300 rounded-md px-3 py-2"
              placeholder="f.eks. 1.2.3.4, 5.6.7.8"
            />
            <span className="text-xs text-kf-gray-500">Komma-separert. La stå blank for å tillate fra alle.</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-kf-gray-700">Notater</span>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full border border-kf-gray-300 rounded-md px-3 py-2"
              placeholder="Hvem er kontakten? Formål?"
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-kf-gray-700 bg-kf-gray-100 hover:bg-kf-gray-200 rounded-md text-sm font-medium">Avbryt</button>
          <button type="submit" className="px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded-md text-sm font-medium">Opprett</button>
        </div>
      </form>
    </div>
  );
}

function NewKeyModal({ username, apiKey, onClose }: { username: string; apiKey: string; onClose: () => void }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-yellow-100 p-2 rounded-full">
            <AlertTriangle className="w-5 h-5 text-yellow-700" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-kf-gray-900">API-nøkkel opprettet</h3>
            <p className="text-sm text-kf-gray-600 mt-1">
              Lagre nøkkelen nå — den vises <strong>kun én gang</strong> og kan ikke hentes ut senere.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase text-kf-gray-500">API-brukernavn</label>
            <code className="block bg-kf-gray-50 border border-kf-gray-200 rounded p-2 text-sm font-mono">{username}</code>
          </div>
          <div>
            <label className="text-xs uppercase text-kf-gray-500">API-nøkkel</label>
            <div className="flex gap-2">
              <code className="flex-1 bg-kf-gray-50 border border-kf-gray-200 rounded p-2 text-xs font-mono break-all">
                {shown ? apiKey : '•'.repeat(50)}
              </code>
              <button type="button" onClick={() => setShown(!shown)} className="px-3 bg-kf-gray-100 hover:bg-kf-gray-200 rounded">
                {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button type="button" onClick={copy} className={`px-3 rounded ${copied ? 'bg-green-100 text-green-800' : 'bg-kf-blue-100 hover:bg-kf-blue-200 text-kf-blue-800'}`}>
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-3 text-xs text-kf-blue-900 mt-4">
          <strong>Bruk i forespørsler:</strong>
          <pre className="mt-1 whitespace-pre-wrap">{`X-API-Username: ${username}
X-API-Key: ${apiKey.slice(0, 20)}...`}</pre>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-4 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded-md py-2 font-medium"
        >
          Jeg har lagret nøkkelen
        </button>
      </div>
    </div>
  );
}
